import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { useDetectClickOutsideElement } from '../../../hooks/useDetectClickOutsideElement';
import { closeModalUpsell } from '../../../actions/searchActions';

import { StarCircle } from '../../../assets/icons/StarCircle.icon';
import { Hospital } from '../../../assets/icons/Hospital.icon';
import { Bagage } from '../../../assets/icons/Bagage.icon';
import { Pharmacy } from '../../../assets/icons/Pharmacy.icon';
import { Phone } from '../../../assets/icons/Phone.icon';
import { ArrowRight } from '../../../assets/icons/ArrowRight.icon';
import { TooltipIcon } from '../../../assets/icons/Tooltip.icon';
import { SalaVipIcon } from '../../../assets/icons/SalaVip.icon';
import { ChipInternacionalIcon } from '../../../assets/icons/ChipInternacional.icon';
import HoverTooltip from '../../../views/v2/common/Tooltip';

import PlanCards from './components/PlanCards';

import { maxValueParcel, isBenefitIncluded } from '../../../utils/plan';

import { formatPrice, getProductDiscount, formatURL, cleanDate } from '../../../utils';
import { setUpsellPurchase } from '../../../actions/purchaseActions';
import dataLayer from '../../../utils/dataLayer';

import styles from './styles.module.css';

const Upsell = ({ closeModalUpsell, upsellPlans, coupon, paymentMethods, match, history, setUpsellPurchase }) => {
  if (!upsellPlans || upsellPlans.length < 2 || !upsellPlans[0] || !upsellPlans[1] || !upsellPlans[0].code || !upsellPlans[1].code) {
    return null;
  }

  const currentPlan = upsellPlans[0];
  const upgradePlan = upsellPlans[1];

  React.useEffect(() => {
    dataLayer.pageView('checkout_upsell');
    dataLayer.upsellViewed(currentPlan, upgradePlan);
  }, []);

  const { containerRef } = useDetectClickOutsideElement({
    onClickOutside: closeModalUpsell,
    enabled: true,
  });

  const pixDiscountMultiplier = useMemo(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const pixPayment = paymentMethods.find((method) => method.slug === 'pix');
      if (pixPayment && pixPayment.discount > 0) {
        return (100 - pixPayment.discount) / 100;
      }
    }
    return 0.95;
  }, [paymentMethods]);

  const calculatePlanPrices = (plan) => {
    if (!plan) return {};

    const basePrice = plan.adult && plan.adult.price;
    const priceWithCouponDiscount = getProductDiscount(basePrice, plan.code, coupon);

    const finalPrice = priceWithCouponDiscount || plan.price_with_coupon_discount || basePrice;

    const parcels = maxValueParcel(finalPrice) || 1;

    return { basePrice, priceWithCouponDiscount, finalPrice, parcels };
  };

  const currentPrices = useMemo(() => calculatePlanPrices(currentPlan), [currentPlan, coupon]);
  const upgradePrices = useMemo(() => calculatePlanPrices(upgradePlan), [upgradePlan, coupon]);

  const getPlanBenefits = (plan) => {
    if (!plan || !plan.benefits) return {};
    return {
      baggage: plan.benefits.find((benefit) => benefit.code === '42' || benefit.code === 42),
      hospital: plan.benefits.find((benefit) => benefit.code === '51' || benefit.code === 51),
      medicament: plan.benefits.find((benefit) => benefit.code === '5' || benefit.code === 5),
      telemedicine: plan.benefits.find((benefit) => benefit.code === '56' || benefit.code === 56),
      salaVip: plan.benefits.find((benefit) => benefit.code === '68' || benefit.code === 68),
      chip: plan.benefits.find((benefit) => benefit.code === '69' || benefit.code === 69),
    };
  };

  const currentBenefits = useMemo(() => getPlanBenefits(currentPlan), [currentPlan]);
  const upgradeBenefits = useMemo(() => getPlanBenefits(upgradePlan), [upgradePlan]);

  const currentHasSalaVip = isBenefitIncluded(currentBenefits.salaVip);
  const upgradeHasSalaVip = isBenefitIncluded(upgradeBenefits.salaVip);
  const currentHasChipInternacional = isBenefitIncluded(currentBenefits.chip);
  const upgradeHasChipInternacional = isBenefitIncluded(upgradeBenefits.chip);

  const hasHighlightBenefits =
    currentHasSalaVip || upgradeHasSalaVip || currentHasChipInternacional || upgradeHasChipInternacional;

  const formatBenefitsCoverage = (benefitCoverage) => {
    if (!benefitCoverage || typeof benefitCoverage !== 'string') return null;
    const match = benefitCoverage.match(/[\d.,]+/);
    if (!match || !match[0]) return benefitCoverage;
    return `U$ ${match[0]}`;
  };

  const comparePlans = () => {
    if (!currentPlan || !upgradePlan || !currentPlan.code || !upgradePlan.code) {
      return;
    }

    const { destination, begin_date, end_date } = match.params;
    const basePath = '/seguro-viagem/comparacao';

    const productsIds = `${currentPlan.code}+${upgradePlan.code}`;
    const url = `${basePath}/${destination}/${cleanDate(begin_date)}/${cleanDate(end_date)}/?products_ids=${productsIds}`;

    setUpsellPurchase(upgradePlan.code);
    history.push(formatURL(match.params, url));
  };

  const calculateUpgradePricePerDay = useMemo(() => {
    const { begin_date, end_date } = match.params;
    if (!begin_date || !end_date) return 0;

    const start = new Date(begin_date);
    const end = new Date(end_date);

    const diffTime = Math.abs(end - start);
    const period = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    if (period <= 0) return 0;

    const priceDiff = ((upgradePrices.finalPrice * pixDiscountMultiplier) - (currentPrices.finalPrice * pixDiscountMultiplier));
    return priceDiff / period;
  }, [match.params, currentPrices.finalPrice, upgradePrices.finalPrice, pixDiscountMultiplier]);

  return (
    <div className={styles.blurBackground}>
      <div className={styles.upsellContainer} ref={containerRef}>
        <div className={styles.bgDecoration} />

        <div className={styles.mobileHeader}>
          <div className={styles.backButton} onClick={closeModalUpsell} style={{ cursor: 'pointer' }}>
            <div style={{ transform: 'rotate(180deg)', display: 'flex' }}>
              <ArrowRight width={16} height={16} />
            </div>
            <span>Olha o que encontramos!</span>
          </div>
        </div>

        {/* Esquerda: Conteúdo de Comparação */}
        <div className={styles.contentContainer}>
          <div className={styles.headerContainer}>
            <div className={styles.headerTitle}>
              <div className={styles.iconContainer}>
                <StarCircle />
              </div>
              <div className={styles.queAchadoText}>
                <p>EXCLUSIVO PARA VOCÊ</p>
              </div>
            </div>

            <div className={styles.mainTitle}>
              <p>
                <span>{`Viaje com `}</span>
                <span className={styles.textGreen}>muito mais segurança</span>
                <span>{` por quase o mesmo preço`}</span>
              </p>
            </div>

            <div className={styles.subTitle}>
              <p>
                <span>{`Por `}</span>
                <span className={styles.textBoldLarge}>R$ {formatPrice(calculateUpgradePricePerDay)}</span>
                <span>{` a mais por dia você `}</span>
                <span className={styles.textBoldLarge}>viaja bem mais tranquilo</span>
              </p>
            </div>
          </div>

          <div className={styles.differencesContainer}>
            <div className={styles.differencesTitle}>
              <p>PRINCIPAIS DIFERENÇAS</p>
            </div>

            {(currentHasSalaVip || upgradeHasSalaVip) && (
              <div className={styles.differenceItemHighlight}>
                <div className={styles.differenceDescription}>
                  <div className={styles.diffIcon}>
                    <SalaVipIcon className={styles.diffIconImg} size={20} />
                  </div>
                  <div className={styles.diffTextRow}>
                    <div className={styles.diffTextHighlight}>
                      <span>Aceso a </span>
                      <strong>Sala VIP</strong>
                    </div>
                    <HoverTooltip content="Espere seu voo em salas VIP de aeroportos: poltronas, wi-fi, comida e bebida inclusos, sem precisar de cartão premium.">
                      <TooltipIcon color="#5A539C" />
                    </HoverTooltip>
                  </div>
                </div>
                <div className={styles.differenceValues}>
                  <div className={styles.valuePill}>{currentHasSalaVip ? 'INCLUSO' : 'NÃO INCLUSO'}</div>
                  <div className={styles.arrowIcon}>
                    <ArrowRight color="#5A539C" />
                  </div>
                  <div className={styles.valuePillHighlight}>{upgradeHasSalaVip ? 'INCLUSO' : 'NÃO INCLUSO'}</div>
                </div>
              </div>
            )}

            {(currentHasChipInternacional || upgradeHasChipInternacional) && (
              <div className={styles.differenceItemHighlight}>
                <div className={styles.differenceDescription}>
                  <div className={styles.diffIcon}>
                    <ChipInternacionalIcon className={styles.diffIconImg} size={20} />
                  </div>
                  <div className={styles.diffTextRow}>
                    <div className={styles.diffTextHighlight}>
                      <span>Inclui </span>
                      <strong>Chip Internacional</strong>
                    </div>
                    <HoverTooltip content="Chip com internet internacional para você usar durante toda a viagem.">
                      <TooltipIcon color="#5A539C" />
                    </HoverTooltip>
                  </div>
                </div>
                <div className={styles.differenceValues}>
                  <div className={styles.valuePill}>{currentHasChipInternacional ? 'INCLUSO' : 'NÃO INCLUSO'}</div>
                  <div className={styles.arrowIcon}>
                    <ArrowRight color="#5A539C" />
                  </div>
                  <div className={styles.valuePillHighlight}>{upgradeHasChipInternacional ? 'INCLUSO' : 'NÃO INCLUSO'}</div>
                </div>
              </div>
            )}

            <div className={styles.differencesRegularGroup}>
              <div className={styles.differenceItem}>
                <div className={styles.differenceDescription}>
                  <div className={styles.diffIcon}>
                    <Hospital />
                  </div>
                  <div className={styles.diffText}>
                    <p>Despesa médica hospitalar (DMH)</p>
                  </div>
                </div>
                <div className={styles.differenceValues}>
                  <div className={styles.valueNormal}>
                    <p>
                      {(currentBenefits.hospital && formatBenefitsCoverage(currentBenefits.hospital.coverage)) || 'Não'}
                    </p>
                  </div>
                  <div className={styles.arrowIcon}>
                    <ArrowRight />
                  </div>
                  <div className={styles.valueGreen}>
                    <p>
                      {(upgradeBenefits.hospital && formatBenefitsCoverage(upgradeBenefits.hospital.coverage)) || 'Não'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.differenceItem} style={hasHighlightBenefits ? { borderBottom: 'none' } : undefined}>
                <div className={styles.differenceDescription}>
                  <div className={styles.diffIcon} style={{ position: 'relative' }}>
                    <Bagage />
                  </div>
                  <div className={styles.diffText}>
                    <p>Seguro de Bagagem Extraviada</p>
                  </div>
                </div>
                <div className={styles.differenceValues}>
                  <div className={styles.valueNormal}>
                    <p>
                      {(currentBenefits.baggage && formatBenefitsCoverage(currentBenefits.baggage.coverage)) || 'Não'}
                    </p>
                  </div>
                  <div className={styles.arrowIcon}>
                    <ArrowRight />
                  </div>
                  <div className={styles.valueGreen}>
                    <p>
                      {(upgradeBenefits.baggage && formatBenefitsCoverage(upgradeBenefits.baggage.coverage)) || 'Não'}
                    </p>
                  </div>
                </div>
              </div>

              {!hasHighlightBenefits && (
                <div className={styles.differenceItem}>
                  <div className={styles.differenceDescription}>
                    <div className={styles.diffIcon}>
                      <Pharmacy />
                    </div>
                    <div className={styles.diffText}>
                      <p>Reembolso de medicamentos</p>
                    </div>
                  </div>
                  <div className={styles.differenceValues}>
                    <div className={styles.valueNormal}>
                      <p>
                        {(currentBenefits.medicament && formatBenefitsCoverage(currentBenefits.medicament.coverage)) ||
                          'Não'}
                      </p>
                    </div>
                    <div className={styles.arrowIcon}>
                      <ArrowRight />
                    </div>
                    <div className={styles.valueGreen}>
                      <p>
                        {(upgradeBenefits.medicament && formatBenefitsCoverage(upgradeBenefits.medicament.coverage)) ||
                          'Não'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!hasHighlightBenefits && (
                <div className={styles.differenceItem} style={{ borderBottom: 'none' }}>
                  <div className={styles.differenceDescription}>
                    <div className={styles.diffIcon}>
                      <Phone />
                    </div>
                    <div className={styles.diffText}>
                      <p>Telemedicina</p>
                    </div>
                  </div>
                  <div className={styles.differenceValues}>
                    <div className={styles.valueNormal}>
                      <p>
                        {(currentBenefits.telemedicine &&
                          formatBenefitsCoverage(currentBenefits.telemedicine.coverage)) ||
                          'Não'}
                      </p>
                    </div>
                    <div className={styles.arrowIcon}>
                      <ArrowRight />
                    </div>
                    <div className={styles.valueGreen}>
                      <p>
                        {(upgradeBenefits.telemedicine &&
                          formatBenefitsCoverage(upgradeBenefits.telemedicine.coverage)) ||
                          'Sim'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.linkContainer}>
                <div className={styles.linkText} onClick={comparePlans}>
                  <p>Clique aqui para ver a comparação completa</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Direita: Cards de Produto */}
        <div className={styles.cardsContainer} style={{ display: 'contents' }}>
          <PlanCards
            upsellPlans={upsellPlans}
            currentPrices={currentPrices}
            upgradePrices={upgradePrices}
            pixDiscountMultiplier={pixDiscountMultiplier}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateProps = (state) => ({
  upsellPlans: state.search.upsellPlans,
  coupon: state.purchase.coupon,
  paymentMethods: state.purchase.paymentMethods,
});

export default withRouter(connect(mapStateProps, { closeModalUpsell, setUpsellPurchase })(Upsell));



// WEBPACK FOOTER //
// ./src/components/V2/upsell/index.jsx